<script setup lang="ts">
  import { computed, onMounted } from 'vue'
  import { Products } from '@/helpers/product'
  import { useModalStore } from '@/stores/modal'
  import { useUpgradeStore } from '@/stores/upgrade'
  import { useBrowserStore } from '@/stores/browser'
  import { useUserStore } from '@/stores/user'
  import { useTourStore } from '@/stores/tour'
  import { track } from '@/helpers/mixpanelDes'
  import DesNavigation from '@/components/navigation/DesNavigation.vue'
  import DesNavigationItem from '@/components/navigation/DesNavigationItem.vue'
  import DesMenu from '@/components/menu/DesMenu.vue'
  import DesMenuItem from '@/components/menu/DesMenuItem.vue'
  import DesNavigationSeparator from '@/components/navigation/DesNavigationSeparator.vue'
  import DesBarMenuUser from '@/components/bar/DesBarMenuUser.vue'
  import DesButton from '@/components/button/DesButton.vue'

  declare const Canny: (...args: any[]) => any

  const user = useUserStore()
  const browser = useBrowserStore()

  onMounted(() => {
    // @ts-expect-error minimized
    // prettier-ignore
    !function(w,d,i,s){function l(){if(!d.getElementById(i)){let f=d.getElementsByTagName(s)[0],e=d.createElement(s);e.type="text/javascript",e.async=!0,e.src="https://canny.io/sdk.js",f.parentNode.insertBefore(e,f)}}if("function"!=typeof w.Canny){var c=function(){c.q.push(arguments)};c.q=[],w.Canny=c,"complete"===d.readyState?l():w.attachEvent?w.attachEvent("onload",l):w.addEventListener("load",l,!1)}}(window,document,"canny-jssdk","script") // eslint-disable-line
    Canny('initChangelog', {
      appID: '5b0f2b723a3efa64491db9d2',
      position: 'bottom',
      align: 'right',
      theme: 'light',
    })
  })
  const modal = useModalStore()
  const upgrade = useUpgradeStore()
  const tour = useTourStore()

  async function logout() {
    await user.performLogout()
    user.logout()
  }

  // DES-4389 Hidden intentionally - test launch materials not needed in menu
  // async function testLaunchMaterials() {
  //   await modal.open(() => import('@/components/modal/DesModalLaunchPrintBooks.vue'))
  //   await modal.open(() => import('@/components/modal/DesModalNewVersionPrintbooks.vue'))
  // }

  async function questionnaireData() {
    await modal.open(() => import('@/components/modal/DesModalQuestionnaireData.vue'))
  }

  function can(action: string): boolean {
    return user.user.permissions[action] === 1
  }

  function helpClicked() {
    track('help-nav-clicked')
    browser.openNewWindowTab('https://intercom.help/designrr/en/')
  }

  function tourClicked() {
    track('tour-nav-clicked')
    tour.show('onboarding')
  }

  function facebookClick() {
    track('facebook-nav-clicked')
    browser.openNewWindowTab(' https://www.facebook.com/groups/439556516151256')
  }

  function youtubeClicked() {
    track('youtube-nav-clicked')
    browser.openNewWindowTab('https://www.youtube.com/channel/UCkvgzp1-EHepnguIDpnL0Sg')
  }

  function cannyClick() {
    track('canny-nav-clicked')
    browser.openNewWindowTab('https://designrr.canny.io/ebooks')
  }

  function castomaticClicked() {
    track('castomatic-nav-clicked')
    document.getElementById('som_record_button_cqeIeclY')?.click()
  }

  const topTierIds = [...user.getIdPremium(), ...user.getIdBusiness(), ...user.getIdAgencyPremium(), Products.Agency]

  const canUpgrade = computed<boolean>(() => {
    const accountType = user.user?.account_type

    return !!accountType && !topTierIds.includes(accountType)
  })

  function upgradeClicked() {
    track('upgrade-nav-clicked')
    upgrade.showUpgradeModal('nav-bottom')
  }

  function promoteClicked() {
    track('promote-nav-clicked')
    browser.openNewWindowTab('https://go.designrr.io/designrr-partner-page')
  }

  function usersStatsClicked() {
    browser.openNewWindowTab('/stats/users_stats')
  }
</script>

<template>
  <DesNavigation>
    <DesMenu right force-flip>
      <template #toggle>
        <DesNavigationItem icon="fa-light fa-circle-question" class="w-full" name="Help" more />
      </template>
      <DesMenuItem
        @click="helpClicked"
        icon="fa-light fa-graduation-cap"
        class="w-80"
        description="Looking to learn about Designrr? Check out our learning center."
        external
      >
        Learning center
      </DesMenuItem>
      <DesMenuItem
        @click="tourClicked"
        class="w-80"
        icon="fa-light fa-flag"
        description="Looking to quickly get started? Take our tour!"
      >
        Take a tour
      </DesMenuItem>
      <DesMenuItem
        @click="youtubeClicked"
        icon="fa-brands fa-youtube"
        class="w-80"
        description="Includes excellent explanatory videos"
        external
      >
        Explore our Youtube Channel
      </DesMenuItem>
      <DesMenuItem
        @click="facebookClick"
        icon="fa-brands fa-square-facebook"
        class="w-80"
        description="Join our community."
        external
      >
        Ask in our Facebook Group
      </DesMenuItem>
      <DesMenuItem
        @click="cannyClick"
        icon="fa-light fa-comment-lines"
        class="w-80"
        description="Have any feedback? We would love to hear from you!"
        external
      >
        Give feedback
      </DesMenuItem>
      <DesMenuItem
        @click="castomaticClicked"
        icon="fa-light fa-video"
        class="w-80"
        description="Have a specific issue you would like us to see? Record your screen."
      >
        Record your screen
      </DesMenuItem>
    </DesMenu>
    <DesNavigationItem icon="fa-light fa-bell" name="What's new" data-canny-changelog more />
    <div
      class="flex items-center gap-2 py-2 px-3 rounded-lg text-sm leading-4 select-none cursor-pointer hover:bg-white-gray"
      data-nav-item
      @click="upgradeClicked()"
    >
      <des-icon icon="fa-light fa-layer-group" class="h-icon w-icon" />
      <span class="text-ellipsis overflow-hidden grow" data-nav-label>Plan</span>
      <DesButton v-if="canUpgrade" compact class="-m-2 text-xs py-1! px-2!">Upgrade</DesButton>
    </div>
    <DesNavigationItem icon="fa-light fa-bullhorn" name="Promote" @click="promoteClicked()" />
    <DesNavigationSeparator />
    <DesMenu right force-flip>
      <template #toggle>
        <DesBarMenuUser />
      </template>
      <DesMenuItem icon="fa-light fa-arrow-right-from-bracket" @click="logout()">Log out</DesMenuItem>
      <DesMenuItem to="/profile" icon="fa-light fa-user" v-des-track="'account-settings-nav-opened'">
        Account settings
      </DesMenuItem>
      <DesMenu right v-if="user.user?.permissions?.superuser === 1">
        <template #toggle>
          <DesMenuItem icon="fa-light fa-user-tie" menu-toggle-ignore more class="text-blue-60">Admin</DesMenuItem>
        </template>
        <DesMenuItem to="/admin/users">Admin (Manage Users)</DesMenuItem>
        <DesMenuItem to="/admin/default-templates-projects">Admin (Default Templates &amp; Projects)</DesMenuItem>
        <DesMenuItem to="/admin/audio-transcript-reports">Admin (Audio Transcriptions)</DesMenuItem>
        <DesMenuItem to="/admin/url-import-statistics">Admin (URL Import Statistics)</DesMenuItem>
        <DesMenuItem @click="usersStatsClicked">Admin (Users Stats)</DesMenuItem>
        <DesMenuItem to="/admin/statistics">Admin (Statistics)</DesMenuItem>
        <DesMenuItem to="/admin/ai-prompts" v-if="user.user?.permissions['ai_prompts.edit']">AI prompts</DesMenuItem>
        <DesMenuItem to="/admin/ai-edit-suggestions">AI edit suggestions</DesMenuItem>
        <DesMenuItem to="/admin/poll-audiobook">Audiobook poll results</DesMenuItem>
        <DesMenuItem to="/admin/refund-bot">Cancellation bot</DesMenuItem>
        <DesMenuItem to="/admin/bot">Cancellation bot old</DesMenuItem>
        <DesMenuItem @click="questionnaireData()">Get questionnaire CSV data</DesMenuItem>
        <DesMenuItem to="/admin/image-themes">Images for themes</DesMenuItem>
        <DesMenuItem to="/admin/poll-import">Import poll results</DesMenuItem>
        <DesMenuItem to="/admin/stripe-prices">Stripe prices (direct payments)</DesMenuItem>
        <DesMenuItem to="/admin/suggestions">Suggestions</DesMenuItem>
        <DesMenuItem to="/admin/templates-themes">Templates themes</DesMenuItem>
        <!-- DES-4389 Hidden intentionally - test launch materials not needed in menu -->
        <!-- <DesMenuItem @click="testLaunchMaterials()">Test launch materials</DesMenuItem> -->
        <DesMenuItem to="/admin/themes-management">Themes</DesMenuItem>
        <DesMenuItem v-if="can('tips.update')" to="/admin/tips">Tips management</DesMenuItem>
        <DesMenuItem v-if="can('tips.update')" to="/admin/top-bars">Top bars</DesMenuItem>
        <DesMenuItem to="/admin/tours">Tours management</DesMenuItem>
        <DesMenuItem to="/admin/variant-niche-images">Variant theme images</DesMenuItem>
      </DesMenu>
    </DesMenu>
  </DesNavigation>
</template>

<style>
  :root .Canny_Badge {
    top: calc(var(--spacing) * 2);
    left: calc(var(--spacing) * 6);
    background-color: var(--color-red-50);
    width: calc(var(--spacing) * 2);
    height: calc(var(--spacing) * 2);
    padding: 0;
  }

  :root #canny-changelog-iframe {
    margin-top: calc(var(--spacing) * -2) !important;
    position: fixed !important;
    left: calc(var(--spacing-navigation) - var(--spacing) * 5) !important;
    transform: translateY(-100%);
    box-shadow: var(--shadow-lg) !important;
  }
</style>
